import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Session, SessionReadState } from '../api/types.js';
import { useApiClient } from './use-api-client.js';

// What is new in a server's sessions for the signed-in user, kept by the API so it follows them from one
// device to another. A session is unread when it was active after the user last read it.

export function useReadState(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['read-state', serverId], queryFn: () => api.sessions.readState(serverId), refetchInterval: 60_000 });
}

/** Activity within this margin of the read mark is the read itself (the page polls a few seconds behind) */
const MARGIN_MS = 5_000;

export function isUnread(session: Pick<Session, 'id' | 'lastActivityAt' | 'createdAt'>, state: SessionReadState | undefined): boolean {
  if (!state) return false;
  const activity = Date.parse(session.lastActivityAt ?? session.createdAt);
  const mark = Date.parse(state.readAt[session.id] ?? state.baseline);
  return Number.isFinite(activity) && Number.isFinite(mark) && activity > mark + MARGIN_MS;
}

export function useMarkRead(serverId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (sessionIds: string[]) => api.sessions.markRead(serverId, sessionIds),
    // Shown read at once; the API's time replaces the local one
    onMutate: (sessionIds) => {
      const now = new Date().toISOString();
      queryClient.setQueryData<SessionReadState>(['read-state', serverId], (prev) => prev
        ? { ...prev, readAt: { ...prev.readAt, ...Object.fromEntries(sessionIds.map((id) => [id, now])) } }
        : prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['read-state', serverId] });
    },
  });
  const { mutate } = mutation;
  return { ...mutation, markRead: useCallback((ids: string[]) => { if (ids.length) mutate(ids.slice(0, 200)); }, [mutate]) };
}
